import React from "react";
import { connect } from "react-redux";
import { RouteComponentProps } from "react-router";
import { Dispatch } from "redux";
import { IAction } from "reduxsauce";
import { IUsersUIData } from "ts/parsers/resolve";

import { IClientPatch, IPatchAction } from "../../components/course-author/helpers/patches";
import SaveInProgress, { ISaveInProgressStateProps } from "../../components/utils/SaveInProgress";
import { SideMenu } from "../../components/utils/SideMenu/SideMenu.container";
import Spinner from "../../components/utils/Spinner";
import SubTopbar from "../../components/utils/SubTopbar";
import TopBar from "../../components/utils/TopBar";
import { RequestState } from "../../reducers/course-author/course-author";
import * as selectors from "../../selectors/selectors";
import { IState } from "../reducer";
import { IClass, ICourse } from "../types/common";
import * as actions from "./actions";
import ClassTabs, { ClassTabOption } from "./tabs/ClassTabs";

interface IClassManagementStateProps {
  accountId: string;
  allCourses: ICourse[];
  allMembers: IUsersUIData[];
  allManagers: IUsersUIData[];
  class: IClass | null;
  classId: string | null;
  userName: string;
  organizationName: string;
  orgId: string;
  fetchStatus: RequestState;
  deleteClassStatus: RequestState;
}

type ClassManagementStateProps = ISaveInProgressStateProps & IClassManagementStateProps;

interface IClassManagementDispatchProps {
  deleteClass: () => void;
  mount: (classId: string) => void;
  unmount: () => void;
  patchClass: (patch: IClientPatch<IPatchAction>) => void;
  fetchClass: () => void;
}

type IClassManagementProps = ClassManagementStateProps &
  IClassManagementDispatchProps &
  RouteComponentProps<{ classId: string }>;

const getInitialTab = (param: string): ClassTabOption => ClassTabOption[param] || ClassTabOption.Goals;

export const ClassManagementComponent: React.FC<IClassManagementProps> = props => {
  const { deleteClassStatus, fetchStatus, match, organizationName, userName } = props;
  const params = new URLSearchParams(props.location.search);

  const notDeletedMembers = props.allMembers.filter(member => !member.deleted);
  const notDeletedManagers = props.allManagers.filter(manager => !manager.deleted);

  const [menuExpanded, setMenuExpanded] = React.useState(true);
  const toggleMenuExpanded = () => setMenuExpanded(!menuExpanded);
  const setSideMenuTabAsSelected = () => {
    const element = document.getElementById("classes-link");
    element?.setAttribute("class", "selected");
  };

  React.useEffect(setSideMenuTabAsSelected, [fetchStatus]);

  React.useEffect(() => {
    props.mount(match.params.classId);
    props.fetchClass();
    return props.unmount;
  }, [props.classId]);

  if (
    props.classId === null ||
    props.class === null ||
    fetchStatus === RequestState.PENDING ||
    deleteClassStatus === RequestState.PENDING
  ) {
    return <Spinner />;
  }

  return (
    <>
      <SaveInProgress firstFetch={props.firstFetch} inProgress={props.inProgress} />
      <TopBar showToggleMenu={true} onToggleMenuClick={toggleMenuExpanded} userName={userName} />
      <SideMenu
        expanded={menuExpanded}
        organizationName={organizationName}
        userName={userName}
        onClose={toggleMenuExpanded}
        isOpen={true}
      />
      <SubTopbar withSidebar={true} isOpen={true}>
        <ClassTabs
          patchClass={props.patchClass}
          allCourses={props.allCourses}
          allMembers={notDeletedMembers}
          allManagers={notDeletedManagers}
          deleteClass={props.deleteClass}
          accountId={props.accountId}
          class={props.class}
          classId={props.classId}
          initialTab={getInitialTab(params.get("tab") || "")}
          orgId={props.orgId}
        />
      </SubTopbar>
    </>
  );
};

const mapStateToProps = (state: IState): ClassManagementStateProps => {
  return {
    accountId: selectors.accountId(state),
    allCourses: state.App.ClassManagement.allCourses,
    allManagers: state.App.ClassManagement.allManagers,
    allMembers: state.App.ClassManagement.allMembers,
    class: selectors.currentClass(state),
    classId: selectors.currentClassId(state),
    deleteClassStatus: state.App.ClassManagement.deleteClassStatus,
    fetchStatus: state.App.ClassManagement.fetchStatus,
    firstFetch: state.App.ClassManagement.class === null,
    inProgress: state.App.ClassManagement.patchClassRequestStatus === RequestState.PENDING,
    orgId: selectors.organizationId(state),
    organizationName: selectors.organizationName(state),
    userName: selectors.accountName(state),
  };
};

const mapDispatchToProps = (dispatch: Dispatch<IAction>): IClassManagementDispatchProps => ({
  deleteClass: () => dispatch(actions.deleteClass()),
  fetchClass: () => dispatch(actions.fetchInitialData()),
  mount: (classId: string) => dispatch(actions.mount(classId)),
  patchClass: (patch: IClientPatch<IPatchAction>) => dispatch(actions.patchClassRequest(patch)),
  unmount: () => dispatch(actions.unmount()),
});

export const ClassManagement = connect(mapStateToProps, mapDispatchToProps)(ClassManagementComponent);
